import React, {useState, useEffect, useRef, memo} from 'react';
import { Typography, LinearProgress } from '@mui/material';

import {publicService} from "../api/services/public";
import {dateStringToTime} from "../utils/dates";
import {useDispatch} from "react-redux";
import {SET_CURRENT_TIME} from "../redux/reducers/public";

interface TimeDisplayProps {
    themeMode: 'light' | 'dark';
}

// Создаем отдельный компонент для отображения времени с локальным состоянием
const TimeDisplay: React.FC<TimeDisplayProps> = React.memo(({themeMode}) => {
    const color = themeMode === 'light' ? 'primary' : 'secondary';
    // Используем локальное состояние вместо Redux
    const [time, setTime] = useState<{ hours: string | number, minutes: string | number, seconds: string | number } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();

    const timeCurrent = useRef(new Date);
    // Эффект для получения начального смещения времени сервера
    useEffect(() => {
        let timeOffset = 0;
        let interval: NodeJS.Timeout;

        // Получаем серверное время только один раз при монтировании
        const getServerTime = async () => {
            try {
                const result = await publicService.getServerTime();
                if (result.data && result.status === "success") {
                    const serverDate = new Date(result.data.now);
                    const localDate = new Date();
                    timeOffset = serverDate.getTime() - localDate.getTime();
                    setIsLoading(false);
                }
            } catch (error) {
                console.error("ERROR", error);
                setIsLoading(false);
            }

            // Запускаем интервал только после получения времени
            startTimer();
        };

        // Функция для обновления времени локально, без Redux
        const updateLocalTime = () => {
            const currentTime = new Date();
            const currentTimeMillis = currentTime.getTime();
            currentTime.setTime(currentTimeMillis + timeOffset);

            const formattedTime = dateStringToTime(currentTime.toISOString());
            setTime(formattedTime);
            timeCurrent.current = currentTime;

            dispatch({
                type: SET_CURRENT_TIME,
                payload: timeCurrent.current
            });
        };

        // Функция запуска таймера
        const startTimer = () => {
            updateLocalTime(); // Вызываем сразу для инициализации
            interval = setInterval(updateLocalTime, 1000);
        };

        getServerTime();

        // Очистка при размонтировании
        return () => {
            if (interval) clearInterval(interval);
        };
    }, []);

    if (isLoading) {
        return <LinearProgress sx={{color: 'white'}}/>;
    }

    if (time) {
        return (
            <Typography id="current-time" sx={{color: 'white'}} data-time={`${time.hours}:${time.minutes}:${time.seconds}`}>
                {time.hours}:{time.minutes}:{time.seconds}
            </Typography>
        );
    }

    return <LinearProgress sx={{color: 'white'}}/>;
});

export default memo(TimeDisplay);
